import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { EventClickArg } from '@fullcalendar/core';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import FollowUp from '../models/FollowUp';
import FollowUpCalendarPopup from "../components/FollowUpCalendarPopup";
import {useAuth} from "../context/AuthContext";
import {getFollowUpsForUser} from "../services/follow-up-service";
import ErrorToast from "../components/ErrorToast";
import Interaction from "../models/Interaction";
import {getAllInteractionsForUser} from "../services/interaction-service";
import InteractionCalendarPopup from "../components/InteractionCalendarPopup";

interface Event {
    id: string,
    title: string,
    start: string,
    extendedProps: {
        event: FollowUp | Interaction,
        type: EventType
    }
}

enum EventType {
    FOLLOW_UP = 'Follow-up',
    INTERACTION = 'Interaction'
}

const CalendarPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [errorToast, setErrorToast] = useState('');
    const [events, setEvents] = useState<Event[]>([]);
    const [selectedFollowUp, setSelectedFollowUp] = useState<FollowUp | null>(null);
    const [selectedInteraction, setSelectedInteraction] = useState<Interaction | null>(null);
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
    
    useEffect(() => {
        if (user) {
            loadEvents();
        }
    }, [user]);

    const loadEvents = async () => {
        const loadedFollowUps = await loadFollowUps();
        const loadedInteractions = await loadInteractions();

        const followUpEvents = followUpsToEvents(loadedFollowUps)
        const interactionEvents = interactionsToEvents(loadedInteractions);

        setEvents([...followUpEvents, ...interactionEvents]);
    }

    const loadInteractions = async (): Promise<Interaction[]> => {
        try {
            if (user && user.id) {
                const response = await getAllInteractionsForUser(user.id);
                if (response.status === 200 && response.data) {
                    return response.data;
                } else {
                    throw new Error('Invalid interaction response');
                }
            } else {
                throw new Error('No user id');
            }
        } catch (err: any) {
            console.error('Error loading interactions', err);
            setErrorToast('Error loading interactions. Please try again later.');
        }
        return [];
    }

    const loadFollowUps = async (): Promise<FollowUp[]> => {
        try {
            if (user && user.id) {
                const response = await getFollowUpsForUser(user.id);
                if (response.status === 200 && response.data) {
                    return response.data;
                } else {
                    throw new Error('Invalid follow-up response');
                }
            } else {
                throw new Error('No user id');
            }
        } catch (err: any) {
            console.error('Error loading follow-ups', err);
            setErrorToast('Error loading follow-ups. Please try again later.');
        }
        return [];
    };

    const interactionsToEvents = (interactions: Interaction[]): Event[] => {
        return interactions.map((interaction) => {
            const dateStr = typeof interaction.interactionDate === 'string'
                ? (interaction.interactionDate as string).split('T')[0]
                : interaction.interactionDate.toISOString().split('T')[0];

            const start = interaction.interactionTime ? `${dateStr}T${interaction.interactionTime}` : dateStr;

            return {
                id: 'interaction-' + String(interaction.id),
                title: interaction.subject,
                start,
                extendedProps: {
                    event: interaction,
                    type: EventType.INTERACTION
                },
            };
        });
    }

    const followUpsToEvents = (followUps: FollowUp[]): Event[] => {
        return followUps.map((followUp) => {
            const dateStr = typeof followUp.followUpDate === 'string'
                ? (followUp.followUpDate as string).split('T')[0]
                : followUp.followUpDate.toISOString().split('T')[0];

            const start = followUp.followUpTime ? `${dateStr}T${followUp.followUpTime}` : dateStr;
            const title = `Follow Up - ${followUp.name}`;

            return {
                id: 'follow-up-' + String(followUp.id),
                title,
                start,
                extendedProps: {
                    event: followUp,
                    type: EventType.FOLLOW_UP
                },
            };
        });
    }

    const handleEventClick = (clickInfo: EventClickArg) => {
        const rect = clickInfo.el.getBoundingClientRect();
        const POPUP_WIDTH = 300;
        const POPUP_HEIGHT = 280;

        // On small screens, center the popup instead of anchoring to the event
        if (window.innerWidth < 640) {
            setPopupPosition({
                top: (window.innerHeight - POPUP_HEIGHT) / 2,
                left: (window.innerWidth - POPUP_WIDTH) / 2,
            });
        } else {
            // Anchor below the event, but flip up if there's not enough room below
            const spaceBelow = window.innerHeight - rect.bottom;
            const top = spaceBelow >= POPUP_HEIGHT
                ? rect.bottom + 6
                : rect.top - POPUP_HEIGHT - 6;

            // Prevent horizontal overflow
            const left = Math.min(rect.left, window.innerWidth - POPUP_WIDTH - 16);

            setPopupPosition({ top, left });
        }
        if (clickInfo.event.extendedProps.type === EventType.FOLLOW_UP) {
            setSelectedInteraction(null);
            setSelectedFollowUp(clickInfo.event.extendedProps.event as FollowUp);
        } else if (clickInfo.event.extendedProps.type === EventType.INTERACTION) {
            setSelectedFollowUp(null);
            setSelectedInteraction(clickInfo.event.extendedProps.event as Interaction)
        }
    };

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />

            <main className="flex-grow container mx-auto px-4 py-6">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-800">Calendar</h1>
                    <p className="text-gray-500 mt-1">
                        View all upcoming and past events across your clients.
                    </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg shadow p-4">
                    <FullCalendar
                        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                        initialView="dayGridMonth"
                        headerToolbar={{
                            left: 'prev,next today',
                            center: 'title',
                            right: 'dayGridMonth,timeGridWeek,listWeek',
                        }}
                        events={events}
                        eventClick={handleEventClick}
                        height="auto"
                        dayMaxEvents={true}
                        eventDidMount={(info) => {
                            info.el.setAttribute('title', info.event.title);
                            info.el.style.cursor = 'pointer';
                        }}
                    />
                </div>
            </main>

            <Footer />

            {selectedFollowUp && (
                <FollowUpCalendarPopup
                    followUp={selectedFollowUp}
                    position={popupPosition}
                    onClose={() => setSelectedFollowUp(null)}
                    onGoToClient={(clientId) => navigate(`/client/${clientId}`)}
                />
            )}
            {selectedInteraction && (
                <InteractionCalendarPopup
                    interaction={selectedInteraction}
                    position={popupPosition}
                    onClose={() => setSelectedInteraction(null)}
                    onGoToInteraction={(interactionId) => navigate(`/interaction/${interactionId}`)}
                />
            )}
            <ErrorToast data-testid="error-toast" message={errorToast} clearError={() => setErrorToast('')} />
        </div>
    );
};

export default CalendarPage;